import { useMemo, useState } from "react";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import {
  ActionIcon,
  Box,
  Card,
  Group,
  NumberInput,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconGripVertical, IconSearch, IconX } from "@tabler/icons-react";
import {
  HOME_SCREEN_MAX_SPEAKERS,
  HOME_SCREEN_SECTION_DEFAULTS,
} from "@congrs/schemas";

import { useGetAllSpeakers } from "../../../../api/speakers";

// The app's home row reads the same first page of speakers, so a speaker this
// list can't reach is one the row couldn't resolve either. Keep in step with
// `HOME_POOL_SIZE` in mobile-next.
const POOL_SIZE = 100;

// Curated picks are dragged inside the tab's own context — nested
// DragDropContexts aren't supported, so `HomeScreenTab` owns the drag, routes
// it by this id (the section key) and hands the move back to `reorder` below.
// The type keeps a pick from being dropped into the section list, and back.
const SPEAKERS_DROPPABLE_ID = "speakers";
const DROPPABLE_TYPE = "home-screen-speakers";

const speakerName = (speaker) =>
  [speaker.firstName, speaker.lastName].filter(Boolean).join(" ");

const useSpeakers = (eventId) => {
  const { data } = useGetAllSpeakers({ eventId, limit: POOL_SIZE });

  return data?.data ?? [];
};

/**
 * A picked id and the speaker it points at, in the organizer's order — one
 * entry per id, including the ones that no longer resolve. Those still take up
 * a place in the saved list, so keeping them here is what lets a drag land on
 * the position the organizer sees, and lets them clear a stale pick.
 */
const usePicked = (eventId, speakerIds) => {
  const speakers = useSpeakers(eventId);

  return useMemo(() => {
    const byId = new Map(
      speakers.map((speaker) => [String(speaker.id), speaker])
    );

    return speakerIds.map((id) => ({ id, speaker: byId.get(String(id)) }));
  }, [speakers, speakerIds]);
};

const pickedLabel = ({ id, speaker }) =>
  speaker ? speakerName(speaker) : `#${id}`;

/** The row's one-line summary: how the row is filled, and with how many. */
const Summary = ({ eventId, options }) => {
  const picked = usePicked(eventId, options.speakerIds);

  if (options.mode === "random") {
    return `Random · ${options.count} ${options.count === 1 ? "speaker" : "speakers"}`;
  }

  if (picked.length === 0) return "No speakers selected · hidden";

  return `Curated · ${picked.map(pickedLabel).join(", ")}`;
};

const RandomSettings = ({ options, onChange }) => (
  <Group gap="xl" wrap="nowrap">
    <Text size="sm" fw={500} w={110} style={{ flexShrink: 0 }}>
      How many
    </Text>
    <NumberInput
      size="xs"
      w={90}
      min={1}
      max={HOME_SCREEN_MAX_SPEAKERS}
      clampBehavior="strict"
      value={options.count}
      onChange={(count) =>
        onChange({ ...options, count: Number(count) || options.count })
      }
    />
    <Text size="xs" c="dimmed">
      A different pick each time the app loads the row.
    </Text>
  </Group>
);

/** The picked speakers, drag-reorderable, each removable. */
const PickedList = ({ picked, options, onChange }) => (
  <Droppable droppableId={SPEAKERS_DROPPABLE_ID} type={DROPPABLE_TYPE}>
    {(provided) => (
      <Stack gap="xs" ref={provided.innerRef} {...provided.droppableProps}>
        {picked.map(({ id, speaker }, index) => (
          <Draggable key={id} draggableId={`speaker-${id}`} index={index}>
            {(dragProvided, snapshot) => (
              <Card
                ref={dragProvided.innerRef}
                {...dragProvided.draggableProps}
                withBorder
                px="sm"
                py={6}
                shadow={snapshot.isDragging ? "md" : undefined}
                style={dragProvided.draggableProps.style}
              >
                <Group gap="sm" wrap="nowrap">
                  <Box
                    {...dragProvided.dragHandleProps}
                    style={{ display: "flex", cursor: "grab" }}
                  >
                    <IconGripVertical size={16} />
                  </Box>
                  <Text
                    size="sm"
                    c={speaker ? undefined : "dimmed"}
                    style={{ flex: 1, minWidth: 0 }}
                    truncate
                  >
                    {speaker ? speakerName(speaker) : `Speaker #${id}`}
                    <Text span c="dimmed" size="xs">
                      {speaker
                        ? speaker.occupation && ` · ${speaker.occupation}`
                        : " · no longer in this event"}
                    </Text>
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={`Remove ${pickedLabel({ id, speaker })}`}
                    onClick={() =>
                      onChange({
                        ...options,
                        speakerIds: options.speakerIds.filter(
                          (pickedId) => String(pickedId) !== String(id)
                        ),
                      })
                    }
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Group>
              </Card>
            )}
          </Draggable>
        ))}
        {provided.placeholder}
      </Stack>
    )}
  </Droppable>
);

/**
 * The speakers not picked yet, revealed while the search field has focus and
 * clicked one by one to add them. A pick keeps both the focus and the term, so
 * picking a handful is a run of clicks rather than a search each time; the
 * list closes when the field is left.
 */
const SpeakerPicker = ({ eventId, options, onChange, isFull }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const speakers = useSpeakers(eventId);

  const available = useMemo(() => {
    const term = search.trim().toLowerCase();

    return speakers
      .filter(
        (speaker) =>
          !options.speakerIds.some((id) => String(id) === String(speaker.id))
      )
      .filter(
        (speaker) => !term || speakerName(speaker).toLowerCase().includes(term)
      );
  }, [speakers, options.speakerIds, search]);

  return (
    <Stack gap="xs">
      <TextInput
        size="xs"
        placeholder="Search speakers"
        leftSection={<IconSearch size={14} />}
        value={search}
        disabled={isFull}
        onChange={(event) => setSearch(event.currentTarget.value)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onKeyDown={(event) => event.key === "Escape" && event.target.blur()}
      />
      {!isOpen ? null : available.length === 0 ? (
        <Text size="xs" c="dimmed">
          {speakers.length === 0
            ? "This event has no speakers yet."
            : search.trim()
              ? `No speakers match “${search.trim()}”.`
              : "Every speaker is already picked."}
        </Text>
      ) : (
        <ScrollArea.Autosize mah={200} type="auto">
          <Stack gap={2} pr="sm">
            {available.map((speaker) => (
              <UnstyledButton
                key={speaker.id}
                disabled={isFull}
                // Keeps the focus on the field, so the list stays open and the
                // next speaker is one more click away.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() =>
                  onChange({
                    ...options,
                    speakerIds: [...options.speakerIds, Number(speaker.id)],
                  })
                }
                style={{ opacity: isFull ? 0.5 : 1 }}
              >
                <Text size="sm" px="sm" py={2} truncate>
                  + {speakerName(speaker)}
                  {speaker.occupation && (
                    <Text span c="dimmed" size="xs">
                      {" "}
                      · {speaker.occupation}
                    </Text>
                  )}
                </Text>
              </UnstyledButton>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}
    </Stack>
  );
};

const CuratedSettings = ({ eventId, options, onChange }) => {
  const picked = usePicked(eventId, options.speakerIds);
  const isFull = options.speakerIds.length >= HOME_SCREEN_MAX_SPEAKERS;

  return (
    <Stack gap="sm">
      {picked.length > 0 && (
        <PickedList picked={picked} options={options} onChange={onChange} />
      )}
      <SpeakerPicker
        eventId={eventId}
        options={options}
        onChange={onChange}
        isFull={isFull}
      />
      <Text size="xs" c="dimmed">
        {isFull
          ? `That's the maximum of ${HOME_SCREEN_MAX_SPEAKERS}. Remove one to add another.`
          : "Shown in this order, up to " +
            `${HOME_SCREEN_MAX_SPEAKERS}. The section is hidden while nothing is picked.`}
      </Text>
    </Stack>
  );
};

const Settings = ({ eventId, options, onChange }) => (
  <Stack gap="md" mt="md" pl={34}>
    <Group gap="xl" wrap="nowrap">
      <Text size="sm" fw={500} w={110} style={{ flexShrink: 0 }}>
        Which speakers
      </Text>
      <SegmentedControl
        size="xs"
        data={[
          { value: "random", label: "Random" },
          { value: "curated", label: "Curated" },
        ]}
        value={options.mode}
        onChange={(mode) => onChange({ ...options, mode })}
      />
    </Group>

    {options.mode === "random" ? (
      <RandomSettings options={options} onChange={onChange} />
    ) : (
      <CuratedSettings
        eventId={eventId}
        options={options}
        onChange={onChange}
      />
    )}
  </Stack>
);

/** Applies a drag within the curated list, which `HomeScreenTab` owns. */
const reorder = (options, from, to) => {
  const speakerIds = Array.from(options.speakerIds);
  const [moved] = speakerIds.splice(from, 1);
  speakerIds.splice(to, 0, moved);

  return { ...options, speakerIds };
};

const speakerSectionSettings = {
  defaults: HOME_SCREEN_SECTION_DEFAULTS.speakers,
  Settings,
  Summary,
  reorder,
};

export default speakerSectionSettings;
